import {
	Button,
	Card,
	Form,
	Input,
	type Job,
	TraitementLong,
} from '@mairistem/composants';
import * as React from 'react';
import './Login.css';
import { getErrorMessage } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import { createPortal } from 'react-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
	CODE_ACCES_SESSION,
	CODE_CLIENT_SESSION,
	CODE_POSTAL_SESSION,
	TOKEN_SESSION,
} from '../../constants/Login';
import type { Auth, Role } from '../../interfaces/auth';
import getUtilisateurs, {
	getStructureJobWithBearer,
} from '../../services/utilisateurs';
import type { ParsedStructureData } from '../../utils/parseStructureJob';
import parseStructureJob from '../../utils/parseStructureJob';
import ModalAdmin from '../Admin/ModalAdmin';

interface LoginProps {
	onAuth: (
		token: string,
		data: ParsedStructureData,
		refuseToBeAdmin?: boolean,
	) => void;
	onSessionAuth: (data: ParsedStructureData) => void;
	userRole: Role;
	setUserRole: (role: Role) => void;
}

const Login = ({
	onAuth,
	onSessionAuth,
	userRole,
	setUserRole,
}: LoginProps) => {
	const [token, setToken] = React.useState<string | null>(null);
	const [isLoadingLogin, setIsLoadingLogin] = React.useState(false);
	const [isAdminMode, setIsAdminMode] = React.useState(false);
	const [job, setJob] = React.useState<Job | null>(null);
	const [pendingAdmin, setPendingAdmin] = React.useState<{
		codeClient: string;
		refuseToBeAdmin: boolean;
	} | null>(null);

	const clearSessionCodes = () => {
		sessionStorage.removeItem(CODE_CLIENT_SESSION);
		sessionStorage.removeItem(CODE_POSTAL_SESSION);
		sessionStorage.removeItem(CODE_ACCES_SESSION);
	};

	const clearAdminSession = () => {
		sessionStorage.removeItem(TOKEN_SESSION);
		setToken(null);
		setPendingAdmin(null);
		setUserRole('user');
	};

	const handleLoadStructure = async () => {
		try {
			const codeClient = sessionStorage.getItem(CODE_CLIENT_SESSION);
			const codePostal = sessionStorage.getItem(CODE_POSTAL_SESSION);
			const codeAcces = sessionStorage.getItem(CODE_ACCES_SESSION);
			if (!codeClient || !codePostal || !codeAcces) return;
			const structureJob = await getUtilisateurs(
				codeClient,
				codePostal,
				codeAcces,
			);
			setJob(structureJob);
		} catch (error) {
			toast.error(getErrorMessage(error, 'Erreur lors de la connexion'));
			clearSessionCodes();
			setIsLoadingLogin(false);
		}
	};

	const handleSubmitSession = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const formData = new FormData(e.currentTarget);
		const codeClient = formData.get('codeClient') as string;
		const codePostal = formData.get('codePostal') as string;
		const codeAcces = formData.get('codeAcces') as string;

		if (!codeClient || !codePostal || !codeAcces) {
			toast.error('Veuillez renseigner tous les champs');
			return;
		}

		if (!/^\d+$/.test(codeClient)) {
			toast.error('Le code client ne doit contenir que des chiffres');
			return;
		}

		if (!/^\d{5}$/.test(codePostal)) {
			toast.error('Le code postal doit contenir exactement 5 chiffres');
			return;
		}

		if (!/^[a-zA-Z0-9]+$/.test(codeAcces)) {
			toast.error(
				'Le code session ne doit contenir que des lettres et des chiffres',
			);
			return;
		}

		// Stocker les codes AVANT l'appel pour que l'interceptor les ajoute
		sessionStorage.setItem(CODE_CLIENT_SESSION, codeClient);
		sessionStorage.setItem(CODE_POSTAL_SESSION, codePostal);
		sessionStorage.setItem(CODE_ACCES_SESSION, codeAcces);

		setIsLoadingLogin(true);
		await handleLoadStructure();
	};

	const handleJobFinish = (finishedJob: Job) => {
		setJob(null);
		setIsLoadingLogin(false);

		if (finishedJob.status === 1) {
			try {
				const parsedData = parseStructureJob(finishedJob.resultat);

				if (pendingAdmin) {
					sessionStorage.setItem(
						'currentCollectivite',
						pendingAdmin.codeClient,
					);
					sessionStorage.setItem(CODE_CLIENT_SESSION, pendingAdmin.codeClient);
					if (!token) return;
					onAuth(token, parsedData, pendingAdmin.refuseToBeAdmin);
					setPendingAdmin(null);
				} else {
					onSessionAuth(parsedData);
				}
			} catch (error) {
				toast.error(
					getErrorMessage(error, 'Erreur lors du traitement des données'),
				);
				if (pendingAdmin) {
					clearAdminSession();
				} else {
					clearSessionCodes();
				}
			}
		} else {
			toast.error(finishedJob.resultat || 'Erreur lors de la connexion');
			if (pendingAdmin) {
				clearAdminSession();
			} else {
				clearSessionCodes();
			}
		}
	};

	const handleJobError = () => {
		setJob(null);
		setIsLoadingLogin(false);
		if (isAdminMode) {
			clearAdminSession();
		} else {
			clearSessionCodes();
		}
		toast.error('Erreur lors de la connexion');
	};

	const handleSubmitAdmin = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const formData = new FormData(e.currentTarget);
		const login = formData.get('login') as string;
		const password = formData.get('password') as string;

		if (!login || !password) {
			toast.error('Veuillez renseigner tous les champs');
			return;
		}

		try {
			setIsLoadingLogin(true);
			const auth = btoa(`${login}:${password}`);
			const response = await request.post<unknown, Auth>('/api/auth', null, {
				headers: {
					Authorization: `Basic ${auth}`,
				},
			});

			if (response.role !== 'admin' && response.role !== 'adminConsult') {
				toast.error(
					"Vous n'êtes pas autorisé à vous connecter en tant qu'administrateur",
				);
				return;
			}

			setToken(response.token ?? null);
			sessionStorage.setItem(TOKEN_SESSION, response.token ?? '');
			setUserRole(response.role);
		} catch (error) {
			toast.error(getErrorMessage(error, 'Erreur lors de la connexion'));
		} finally {
			setIsLoadingLogin(false);
		}
	};

	const handleValidateAdmin = async (
		codeClient: string,
		refuseToBeAdmin: boolean,
	) => {
		setPendingAdmin({ codeClient, refuseToBeAdmin });
		setIsLoadingLogin(true);

		try {
			if (!token) return;
			const structureJob = await getStructureJobWithBearer(token, codeClient);
			setJob(structureJob);
		} catch (error) {
			toast.error(getErrorMessage(error, 'Erreur lors de la connexion'));
			clearAdminSession();
			setIsLoadingLogin(false);
		}
	};

	const handleToggleAdminMode = () => {
		if (isAdminMode) {
			// Quitte le mode admin → nettoyer toute session admin en cours
			clearAdminSession();
		}
		setIsAdminMode(!isAdminMode);
	};

	return (
		<div className="login-container">
			{createPortal(<ToastContainer />, document.body)}
			<Card raised className="login-card">
				<Card.Header>
					<div className="login-title">iXchange Pass Élu</div>
					<div>Connexion à votre espace collectivité</div>
				</Card.Header>
				<Card.Content>
					<Form
						onSubmit={isAdminMode ? handleSubmitAdmin : handleSubmitSession}
					>
						{isAdminMode ? (
							<>
								<Form.Input
									fluid
									name="login"
									control={Input}
									data-testid="login"
									label="Identifiant"
									required
								/>
								<Form.Input
									fluid
									name="password"
									control={Input}
									data-testid="password"
									label="Mot de passe"
									type="password"
									required
								/>
							</>
						) : (
							<>
								<Form.Input
									fluid
									name="codeClient"
									control={Input}
									data-testid="codeClient"
									label="Code client"
									required
									pattern="[0-9]*"
									inputMode="numeric"
								/>
								<Form.Input
									fluid
									name="codePostal"
									data-testid="codePostal"
									label="Code postal"
									required
									pattern="[0-9]{5}"
									inputMode="numeric"
									maxLength={5}
								/>
								<Form.Input
									fluid
									name="codeAcces"
									control={Input}
									data-testid="codeAcces"
									label="Code session"
									required
								/>
							</>
						)}
						<Form.Checkbox
							className="admin-mode-toggle"
							label="Connexion administrateur"
							checked={isAdminMode}
							onChange={handleToggleAdminMode}
							data-testid="adminModeCheckbox"
						/>
						<Button
							className="login-button"
							primary
							fluid
							type="submit"
							loading={isLoadingLogin}
						>
							Se connecter
						</Button>
					</Form>
				</Card.Content>
			</Card>

			{userRole !== 'user' && !job && (
				<ModalAdmin onValidate={handleValidateAdmin} />
			)}

			{job && (
				<TraitementLong
					title="Connexion en cours"
					identifiantJob={job.identifiant}
					onFinish={handleJobFinish}
					onError={handleJobError}
					url="/api/job360"
				/>
			)}
		</div>
	);
};
export default Login;
